import { useCallback, useEffect, useMemo, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon, DropdownMenu, FormattedNumber } from '../../index';

export interface TablePaginationProps {
  isLoading?: boolean;
  count?: number;
  itemText?: string;
  pageSize?: number;
  page?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number, size: number) => void;
}

interface PageSizeControlProps {
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (pageSize: number) => void;
}

interface PageNumberDisplayProps {
  isLoading?: boolean;
  page: number;
  pageSize: number;
  count: number;
  itemText: string;
}

interface PaginationButtonProps {
  disabled: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isLoading: boolean;
}

/* Styles */
const wrapperClasses =
  'flex items-center justify-items-end w-full text-sm leading-6 text-gray-900 p-2 bg-white border-t border-gray-300 ';
const buttonClasses = 'text-base size-6 hover:bg-gray-100 active:bg-gray-200 rounded-lg';
const buttonDisabledClasses = `${buttonClasses} text-gray-500 active:bg-gray-100`;
const buttonIconClasses = 'h-6 w-6 fill-current';
const dividerClasses = 'border-l mx-4 border-gray-300 h-8';
const dropdownWrapper = 'ml-3';
const pageNumberDisplayClasses = 'mr-3';
const pageSizeControlClasses = 'ml-auto';
const dropdownMenuItemClasses = 'py-2.5 w-16 px-5 text-right hover:bg-jungle-50 select-none';
const skeletonClasses = 'h-6 bg-gray-100 rounded-lg mb-1';
const pageNumberSkeletonClasses = `${skeletonClasses} w-28`;
const pageControlskeletonClasses = `${skeletonClasses} w-6 ml-1`;

/* Functions */
const getRange = (count: number, page: number, pageSize: number) => {
  const start = 1 + pageSize * (page - 1);
  const end = start + pageSize - 1;
  return end > count ? [start, count] : [start, end];
};

const getIsLastPage = (count: number, page: number, pageSize: number) => {
  return getRange(count, page, pageSize)[1] == count;
};

/* Child components */
const ForwardButton = (props: PaginationButtonProps) => {
  const { disabled, onClick, isLoading } = props;

  if (isLoading) {
    return <div className={pageControlskeletonClasses} />;
  }

  return (
    <button
      aria-label="next-button"
      className={disabled ? buttonDisabledClasses : buttonClasses}
      onClick={(e) => !disabled && onClick(e)}
    >
      <ChevronRightIcon className={buttonIconClasses} />
    </button>
  );
};

const PreviousButton = (props: PaginationButtonProps) => {
  const { disabled, onClick, isLoading } = props;

  if (isLoading) {
    return <div className={pageControlskeletonClasses} />;
  }

  return (
    <button
      aria-label="prev-button"
      className={disabled ? buttonDisabledClasses : buttonClasses}
      onClick={(e) => !disabled && onClick(e)}
    >
      <ChevronLeftIcon className={buttonIconClasses} />
    </button>
  );
};

const PageNumberDisplay = (props: PageNumberDisplayProps) => {
  const { page, pageSize, count, itemText, isLoading } = props;
  const [start, end] = useMemo(() => getRange(count, page, pageSize), [count, page, pageSize]);

  if (isLoading) {
    return <span className={pageNumberSkeletonClasses} />;
  }
  return (
    <span className={pageNumberDisplayClasses}>
      <FormattedNumber>{start}</FormattedNumber>-<FormattedNumber>{end}</FormattedNumber> of{' '}
      <FormattedNumber>{count}</FormattedNumber> {itemText}
    </span>
  );
};

const PageSizeControl = (props: PageSizeControlProps) => {
  const { pageSize, pageSizeOptions, onPageSizeChange } = props;

  const handleOnClick = useCallback(
    (size: number) => () => {
      if (!onPageSizeChange) return;
      onPageSizeChange(size);
    },
    [onPageSizeChange]
  );

  return (
    <div className={pageSizeControlClasses}>
      Items per page:{' '}
      <span className={dropdownWrapper}>
        <DropdownMenu
          closeOnSelect={true}
          buttonContent={<div data-testid="page-size-control">{pageSize}</div>}
          menuContent={
            <div aria-label="group" data-testid="page-size-dropdown">
              {pageSizeOptions.map((v) => (
                <div aria-label="menuitem" className={dropdownMenuItemClasses} onClick={handleOnClick(v)} key={v}>
                  {v}
                </div>
              ))}
            </div>
          }
        />
      </span>
    </div>
  );
};

/* Main component */
export const TablePagination = ({
  page = 1,
  isLoading = false,
  pageSize = 25,
  count = 0,
  pageSizeOptions = [10, 25, 50, 100, 500],
  itemText = 'items',
  ...props
}: TablePaginationProps) => {
  const { onPageChange } = props;

  const [pageData, setPageData] = useState<{ page: number; size: number }>({
    page,
    size: pageSize,
  });

  const { page: currentPage, size } = pageData;

  const isLastPage = useMemo(() => getIsLastPage(count, currentPage, size), [count, currentPage, size]);

  // Allow the consumer to control page number and page size
  useEffect(() => setPageData((prev) => ({ ...prev, size: pageSize })), [pageSize]);
  useEffect(() => setPageData((prev) => ({ ...prev, page })), [page]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPageData((prev) => ({ ...prev, page }));
      if (!onPageChange) return;
      onPageChange(page, size);
    },
    [size, onPageChange]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageData({ page: 1, size });
      if (!onPageChange) return;
      onPageChange(1, size);
    },
    [onPageChange]
  );

  return (
    <div className={wrapperClasses}>
      <PageSizeControl pageSize={size} pageSizeOptions={pageSizeOptions} onPageSizeChange={handlePageSizeChange} />
      <div className={dividerClasses} />
      <PageNumberDisplay isLoading={isLoading} page={currentPage} pageSize={size} count={count} itemText={itemText} />
      <PreviousButton
        disabled={currentPage == 1}
        onClick={() => handlePageChange(currentPage - 1)}
        isLoading={isLoading}
      />
      <ForwardButton disabled={isLastPage} onClick={() => handlePageChange(currentPage + 1)} isLoading={isLoading} />
    </div>
  );
};

export default TablePagination;
